import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { postDeleteListing } from '../../util/api';
import { NOT_DELETED_QUERY_PARAMS } from '../../util/deletedListing';
import { createImageVariantConfig } from '../../util/sdkLoader';
import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { parse } from '../../util/urlHelpers';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

// The listing states the status tabs filter by. They go to the API as `states`,
// and to the page URL as `?status=`.
export const LISTING_STATUSES = [
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
];

// A status of null means "every listing" (the "All" tab).
const ALL_STATUSES = [null, ...LISTING_STATUSES];

export const statusCountKey = status => status || 'all';
const cacheKey = (status, page) => `${statusCountKey(status)}:${page}`;
// Every query leaves out the listings the seller deleted (P25028-236), so the pages
// and the tab counts both skip them.
const statusFilter = status => ({
  ...NOT_DELETED_QUERY_PARAMS,
  ...(status ? { states: status } : {}),
});

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';
export const SHOW_CACHED_LISTINGS = 'app/ManageListingsPage/SHOW_CACHED_LISTINGS';

export const FETCH_STATUS_COUNT_SUCCESS = 'app/ManageListingsPage/FETCH_STATUS_COUNT_SUCCESS';
export const CLEAR_LISTINGS_CACHE = 'app/ManageListingsPage/CLEAR_LISTINGS_CACHE';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const DELETE_LISTING_REQUEST = 'app/ManageListingsPage/DELETE_LISTING_REQUEST';
export const DELETE_LISTING_SUCCESS = 'app/ManageListingsPage/DELETE_LISTING_SUCCESS';
export const DELETE_LISTING_ERROR = 'app/ManageListingsPage/DELETE_LISTING_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  deletingListing: null,
  deletingListingError: null,
  // True after a delete, for the "Listing deleted" message. Cleared when the seller
  // moves to another tab or page.
  listingDeleted: false,

  // Every listings query gets a new id. Only the answer to the newest one is
  // put on screen, so a slow answer for a tab the seller already left is dropped.
  latestQueryId: 0,
  // Pages already loaded during this visit, keyed by status + page number:
  // { 'published:1': { ids, pagination } }.
  resultCache: {},
  // Bumped whenever cached data goes out of date. Answers to queries sent before
  // the bump are not written to the cache or the counts.
  cacheVersion: 0,
  // The server's total for each tab, e.g. { all: 113, published: 113, draft: 0 }.
  statusCounts: {},
};

const resultIds = data => data.data.map(l => l.id);

// Forget what is known about the given statuses, so the next look at them asks
// the server again.
const invalidateStatuses = (state, statuses) => {
  const keys = statuses.map(statusCountKey);
  const isAffected = key => keys.includes(key.split(':')[0]);
  const resultCache = Object.keys(state.resultCache)
    .filter(key => !isAffected(key))
    .reduce((cache, key) => ({ ...cache, [key]: state.resultCache[key] }), {});
  const statusCounts = Object.keys(state.statusCounts)
    .filter(key => !keys.includes(key))
    .reduce((counts, key) => ({ ...counts, [key]: state.statusCounts[key] }), {});
  return { ...state, resultCache, statusCounts, cacheVersion: state.cacheVersion + 1 };
};

// Opening or closing a listing moves it from one tab to another. The "All" tab
// keeps the same listings, and its cards read the updated entity, so it stays.
const invalidateStatusChange = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing?.[listingEntity.id.uuid];
  const oldStatus = oldListing?.attributes?.state;
  const newStatus = listingEntity.attributes?.state;
  const changed = [oldStatus, newStatus].filter(s => !!s);
  return oldStatus === newStatus ? state : invalidateStatuses(state, changed);
};

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload, meta } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        latestQueryId: meta.queryId,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
        listingDeleted: false,
      };
    case SHOW_CACHED_LISTINGS: {
      const { ids, pagination } = state.resultCache[meta.cacheKey];
      return {
        ...state,
        queryParams: payload.queryParams,
        latestQueryId: meta.queryId,
        queryInProgress: false,
        queryListingsError: null,
        currentPageResultIds: ids,
        pagination,
        listingDeleted: false,
      };
    }
    case FETCH_LISTINGS_SUCCESS: {
      const { queryId, cacheKey: key, status, cacheVersion } = meta;
      const ids = resultIds(payload.data);
      const pagination = payload.data.meta;

      // A late answer is still true for its own tab, so it is kept for later.
      const cached =
        cacheVersion === state.cacheVersion
          ? {
              resultCache: { ...state.resultCache, [key]: { ids, pagination } },
              statusCounts: {
                ...state.statusCounts,
                [statusCountKey(status)]: pagination.totalItems,
              },
            }
          : {};
      const shown =
        queryId === state.latestQueryId
          ? { currentPageResultIds: ids, pagination, queryInProgress: false }
          : {};
      return { ...state, ...cached, ...shown };
    }
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      if (meta.queryId !== state.latestQueryId) {
        return state;
      }
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case FETCH_STATUS_COUNT_SUCCESS:
      if (meta.cacheVersion !== state.cacheVersion) {
        return state;
      }
      return {
        ...state,
        statusCounts: { ...state.statusCounts, [statusCountKey(payload.status)]: payload.count },
      };

    case CLEAR_LISTINGS_CACHE:
      // The list on screen goes too, so the next visit doesn't flash this
      // visit's cards, and an answer still on its way is not shown.
      return {
        ...state,
        pagination: null,
        queryParams: null,
        queryInProgress: false,
        queryListingsError: null,
        currentPageResultIds: [],
        latestQueryId: state.latestQueryId + 1,
        resultCache: {},
        statusCounts: {},
        cacheVersion: state.cacheVersion + 1,
        listingDeleted: false,
      };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(invalidateStatusChange(state, payload.data), payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(invalidateStatusChange(state, payload.data), payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case DELETE_LISTING_REQUEST:
      return {
        ...state,
        deletingListing: payload.listingId,
        deletingListingError: null,
        listingDeleted: false,
      };
    case DELETE_LISTING_SUCCESS: {
      // The card goes at once. Any tab and count may hold the listing, so all of them
      // are asked for again. Other pages then come fresh from the server, which keeps
      // the paging right.
      const { listingId } = payload;
      const pagination = state.pagination
        ? { ...state.pagination, totalItems: Math.max(0, state.pagination.totalItems - 1) }
        : state.pagination;
      return {
        ...invalidateStatuses(state, ALL_STATUSES),
        currentPageResultIds: state.currentPageResultIds.filter(id => id.uuid !== listingId.uuid),
        pagination,
        deletingListing: null,
        listingDeleted: true,
      };
    }
    case DELETE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        deletingListing: null,
        deletingListingError: {
          listingId: state.deletingListing,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const deleteListingRequest = listingId => ({
  type: DELETE_LISTING_REQUEST,
  payload: { listingId },
});

export const deleteListingSuccess = listingId => ({
  type: DELETE_LISTING_SUCCESS,
  payload: { listingId },
});

export const deleteListingError = e => ({
  type: DELETE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = (queryParams, meta) => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
  meta,
});

export const showCachedListings = (queryParams, meta) => ({
  type: SHOW_CACHED_LISTINGS,
  payload: { queryParams },
  meta,
});

export const queryListingsSuccess = (response, meta) => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
  meta,
});

export const queryListingsError = (e, meta) => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
  meta,
});

export const fetchStatusCountSuccess = (status, count, meta) => ({
  type: FETCH_STATUS_COUNT_SUCCESS,
  payload: { status, count },
  meta,
});

// Called when the seller leaves the page. Listings can change elsewhere (edit,
// publish, bulk upload), so the next visit starts from the server again.
export const clearListingsCache = () => ({ type: CLEAR_LISTINGS_CACHE });

/**
 * Show one page of one status tab. A page already loaded during this visit is
 * shown from the cache, with no request.
 *
 * Throws on error for new queries (loadData may need that info).
 *
 * @param {Object} queryParams `status` (a listing state, or null for all
 * listings), `page`, and the other SDK query params (perPage, include, ...)
 */
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  const { status = null, page = 1, ...rest } = queryParams;
  const key = cacheKey(status, page);
  const { resultCache, latestQueryId, cacheVersion } = getState().ManageListingsPage;
  const queryId = latestQueryId + 1;

  const cached = resultCache[key];
  if (cached) {
    dispatch(showCachedListings(queryParams, { queryId, cacheKey: key }));
    return Promise.resolve(cached);
  }

  dispatch(queryListingsRequest(queryParams, { queryId }));

  return sdk.ownListings
    .query({ ...rest, page, ...statusFilter(status) })
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response, { queryId, cacheKey: key, status, cacheVersion }));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e), { queryId }));
      throw e;
    });
};

// Only `meta.totalItems` is read, so one listing and no images are asked for.
const fetchStatusCount = status => (dispatch, getState, sdk) => {
  const { cacheVersion } = getState().ManageListingsPage;
  return sdk.ownListings
    .query({ page: 1, perPage: 1, ...statusFilter(status) })
    .then(response => {
      const count = response.data.meta.totalItems;
      dispatch(fetchStatusCountSuccess(status, count, { cacheVersion }));
    })
    .catch(e => {
      // The tab still works without its number, so this is not shown to the seller.
      // eslint-disable-next-line no-console
      console.error(storableError(e));
    });
};

/**
 * Fetch the tab counts that are not known yet. Each status is its own small
 * request, and they run side by side.
 *
 * @param {Array<String|null>} statuses statuses to count; null is all listings
 */
export const fetchStatusCounts = (statuses = ALL_STATUSES) => (dispatch, getState) => {
  const { statusCounts } = getState().ManageListingsPage;
  const missing = statuses.filter(status => statusCounts[statusCountKey(status)] == null);
  return Promise.all(missing.map(status => dispatch(fetchStatusCount(status))));
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(closeListingSuccess(response));
      // The success action dropped the counts of the tabs the listing moved between.
      dispatch(fetchStatusCounts());
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      // The success action dropped the counts of the tabs the listing moved between.
      dispatch(fetchStatusCounts());
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

// P25028-236. The server checks that the listing is the seller's own, then discards a
// draft, or closes and marks any other listing. See server/api/delete-listing.js.
export const deleteListing = listingId => dispatch => {
  dispatch(deleteListingRequest(listingId));

  return postDeleteListing({ listingId })
    .then(response => {
      dispatch(deleteListingSuccess(listingId));
      // The success action dropped every tab count.
      dispatch(fetchStatusCounts());
      return response;
    })
    .catch(e => {
      dispatch(deleteListingError(storableError(e)));
    });
};

export const loadData =(params, search, config) => dispatch => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  const status = LISTING_STATUSES.includes(queryParams.status) ? queryParams.status : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const listingsLoaded = dispatch(
    queryOwnListings({
      status,
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['images', 'currentStock'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    })
  );

  // The other tabs' counts load in the background. The query above already
  // brings the count of the tab being shown.
  dispatch(fetchStatusCounts(ALL_STATUSES.filter(s => s !== status)));

  return listingsLoaded;
};
